<template lang="pug">
  .charts-wrapper.mb-4(
    v-loading="loadingStatistics"
  )
    .filters
      .fields-info-box(v-if="Array.isArray(fieldsInfoSettings) && fieldsInfoSettings.length > 0")
        .description
          | {{ $t('sensors.fields_info_settings.actions.description.optimal_ranges', { field: fieldName }) }}
        .fields-summary
          span(
            v-for="(item, i) in fieldsInfoSettings"
            :key="item.id"
            class="field-tag"
          )
            | {{ item.info_type.toUpperCase() }}: {{ item.range1s }} - {{ item.range1e }} {{ item.info_type_unit }}
      FilterDatePicker(
        v-model="dateRange"
        :resetSignal="resetKey"
        @onChange="handleChange"
      )
      el-button.el-icon-refresh-left.mr-2.cursor-pointer(
        style="font-size: 35px; margin-right: 0px;"
        :class="{disabled: generatePausedForSeconds}"
        :loading="generatePausedForSeconds"
        @click="reloadData()")
    br
    SensorChart(
      v-if="sensorInfos"
      :chartData="sensorInfos"
      @onZoom="handleZoom"  
    )
</template>

<script>
import SensorChart from "@components/charts/SensorChart.vue";
import FilterDatePicker from "@components/_shared/FilterDatePicker";
import { mapActions, mapState } from "vuex";
import _ from "lodash";

export default {
  name: "ViewSensor",

  components: {
    SensorChart,
    FilterDatePicker,
  },

  data() {
    return {
      sensorInfos: null,
      dateRange: [],
      fieldsInfoSettings: [],
      loadingStatistics: false,
      generatePausedForSeconds: false,
      loading: false,
      reload: false,
      resetKey: null,
      fieldName: "",
    };
  },

  computed: {
    ...mapState("auth", ["user"]),
    ...mapState("soilSensor", ["soilSensor"]),
    ...mapState("transmitter", ["transmitter"]),
  },

  watch: {
    async reload(val) {
      if (val) {
        this.handleLoading(true);
        await this.handleChange(this.dateRange);
        this.handleLoading(false);
      }
    }
  },

  async created() {
    await this.handleGetField();
    await this.handleChange(this.dateRange);
  },

  methods: {
    ...mapActions("field", ["getFieldInfoSettings"]),
    ...mapActions("sensorInfo", ['getSoilSensorInfo']),
    ...mapActions("transmitter", ["getTransmitter"]),

    handleLoading(loading) {
      this.loading = loading;
      if(!loading) {
        setTimeout(() => this.generatePausedForSeconds = loading, 2000);
      }else {      
        this.generatePausedForSeconds = loading;
      }
      this.reload = false;
    },

    reloadData(){
      this.resetKey = Date.now();
      this.reload = true;
    },

    async handleZoom([start, end]) {
      console.log('Parent received zoom:', start, end);
      this.dateRange = [start, end];
      await this.handleChange(this.dateRange);
    },

    async handleChange(values) {
      this.sensorInfos = [];
      this.loadingStatistics = true;

      let computedParams = `?by_soil_sensor_id=${this.soilSensor.id}`;
      if (values && values.length > 0) {
        let dateRangeComputed = {
          start_date: values[0],
          end_date: values[1],
        };
        computedParams += `&by_date_range[start_date]=${dateRangeComputed.start_date}&by_date_range[end_date]=${dateRangeComputed.end_date}`;
        this.dateRange = [dateRangeComputed.start_date, dateRangeComputed.end_date];
        
        const response = await this.getSoilSensorInfo(computedParams);
        this.sensorInfos = response;
      } 
      else {
        this.sensorInfos = [];
      }

    this.loadingStatistics = false;

    },

    async handleGetField(){
      this.fieldsInfoSettings = [];
      const transmitterResponse = await this.getTransmitter(this.soilSensor.sensor_transmitter.id);

      if( transmitterResponse.data && transmitterResponse.data.field) {
        let fieldId = transmitterResponse.data.field.id;
        this.fieldName = transmitterResponse.data.field_name;
        this.handleSoilSensorType(fieldId);
        const response = await this.getFieldInfoSettings(this.handleSoilSensorType(fieldId));

        this.fieldsInfoSettings = response.data.map((item) => ({
          id: item.id,
          info_type: item.info_type || '',
          info_type_unit: item.info_type_unit || '',
          range1s: parseFloat(item.range1s) || 0,
          range1e: parseFloat(item.range1e) || 0,
          range1color: item.range1color || '',
          range2s: parseFloat(item.range2s) || 0,
          range2e: parseFloat(item.range2e) || 0,
          range2color: item.range2color || '',
          range3s: parseFloat(item.range3s) || 0,
          range3e: parseFloat(item.range3e) || 0,
          range3color: item.range3color || ''
        }));
      }
      
    },

    handleSoilSensorType(fieldId) {
      let computedParams = `?by_field_id=${fieldId}&by_info_type=`;

      if(["ee", "de", "c0", "e0", "f0", "d0", "fe"].includes(this.soilSensor.sensor_type)) {
        computedParams += ',humidity,temperature';
      }
      if(["ee", "e0", "f0", "fe"].includes(this.soilSensor.sensor_type)) {
        computedParams += ',conductivity';
      }
      if(["de", "10", "f0", "d0", "fe"].includes(this.soilSensor.sensor_type)) {
        computedParams += ',ph';
      }
      if(["0e", "ee", "de", "fe"].includes(this.soilSensor.sensor_type)) {
        computedParams += ',nitrogen,phosphorus,potassium';
      }
      return computedParams;
    }
  },
};
</script>
<style lang="scss" scoped>
.fields-info-box {
  border: 1px solid #e1f3d8;
  background-color: #fcfffa;
  padding: 16px;
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);

  .description {
    font-family: inherit;       /* use the default app font */
    font-weight: 500;           /* semi-bold, similar to date label */
    font-size: 14px;            /* match surrounding font size */
    color: #2d3a4a;             /* slightly darker for visibility, not black */
    margin-bottom: 0.75rem;
  }

  .fields-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 14px;

    .field-tag {
      background-color: #f6ffed;
      // border: 1px solid #b7eb8f;
      color: #6aa84f;
      padding: 6px 12px;
      border-radius: 8px;
      font-size: 13.5px;
      font-weight: 500;
      white-space: nowrap;
    }
  }
}
</style>